import { useMemo, useState } from "react"
import { Button } from "@f2e/ui/components/base"
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@f2e/ui/components/base"
import { useStore } from "@/store"

const TestOAuthBack = () => {
  const serverRoot = useStore((state) => state.sysConfig.serverRoot)

  const [apiTestLoading, setApiTestLoading] = useState<string | null>(null)
  const [apiTestResponse, setApiTestResponse] = useState<unknown>(null)
  const [apiTestError, setApiTestError] = useState<string | null>(null)
  const [lastTestEndpoint, setLastTestEndpoint] = useState<string | null>(null)

  const baseUrl = useMemo(() => {
    if (!serverRoot) return ""
    return serverRoot.endsWith("/") ? serverRoot.slice(0, -1) : serverRoot
  }, [serverRoot])

  const testApiCall = async (
    endpoint: string,
    method: string = "GET",
    body?: unknown
  ) => {
    if (!serverRoot) {
      setApiTestError("Server root not configured")
      return
    }

    setApiTestLoading(endpoint)
    setApiTestError(null)
    setApiTestResponse(null)
    setLastTestEndpoint(endpoint)

    try {
      const url = endpoint.startsWith("http")
        ? endpoint
        : `${baseUrl}${endpoint}`
      const options: RequestInit = {
        method,
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      }

      if (body && method !== "GET") {
        options.body = JSON.stringify(body)
      }

      const response = await fetch(url, options)

      let data
      const contentType = response.headers.get("content-type")
      if (contentType && contentType.includes("application/json")) {
        data = await response.json()
      } else {
        const text = await response.text()
        setApiTestError(
          `HTTP ${response.status}: Expected JSON but got ${contentType || "unknown"}\n\nResponse preview:\n${text.substring(0, 500)}`
        )
        setApiTestLoading(null)
        return
      }

      if (!response.ok) {
        if (response.status === 401) {
          setApiTestError(
            "401 Unauthorized. If you just logged in, the cookie might not be set/sent. Try logging in again and verify cookies for the API domain."
          )
          return
        }
        setApiTestError(
          `HTTP ${response.status}: ${JSON.stringify(data, null, 2)}`
        )
      } else {
        setApiTestResponse(data)
      }
    } catch (error) {
      setApiTestError(error instanceof Error ? error.message : String(error))
    } finally {
      setApiTestLoading(null)
    }
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 p-4">
      <Card className="w-full max-w-4xl">
        <CardHeader>
          <CardTitle>Test OAuth Callback (Dev Only)</CardTitle>
          <p className="text-sm text-muted-foreground mt-2">
            With httpOnly cookies enabled, the JWT is no longer available to
            JavaScript. This page validates auth by calling APIs with cookies.
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <h3 className="font-semibold text-sm">API Tests:</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <Button
                onClick={() => testApiCall("/api/v1/chats?limit=10&offset=0")}
                disabled={!!apiTestLoading}
                variant="outline"
                size="sm"
              >
                {apiTestLoading === "/api/v1/chats?limit=10&offset=0"
                  ? "Testing..."
                  : "GET /api/v1/chats"}
              </Button>
              <Button
                onClick={() =>
                  testApiCall("/api/v1/histories?limit=10&offset=0")
                }
                disabled={!!apiTestLoading}
                variant="outline"
                size="sm"
              >
                {apiTestLoading === "/api/v1/histories?limit=10&offset=0"
                  ? "Testing..."
                  : "GET /api/v1/histories"}
              </Button>
              <Button
                onClick={() => testApiCall("/api/v1/agents")}
                disabled={!!apiTestLoading}
                variant="outline"
                size="sm"
              >
                {apiTestLoading === "/api/v1/agents"
                  ? "Testing..."
                  : "GET /api/v1/agents"}
              </Button>
              <Button
                onClick={() => testApiCall("/api/v1/organizations")}
                disabled={!!apiTestLoading}
                variant="outline"
                size="sm"
              >
                {apiTestLoading === "/api/v1/organizations"
                  ? "Testing..."
                  : "GET /api/v1/organizations"}
              </Button>
            </div>

            {lastTestEndpoint && (
              <p className="text-xs text-muted-foreground">
                Last tested:{" "}
                <span className="font-mono">{lastTestEndpoint}</span>
              </p>
            )}

            {apiTestResponse !== null && (
              <div className="space-y-2 mt-4">
                <h4 className="font-semibold text-xs text-green-600">
                  API Response (Success) - {lastTestEndpoint}:
                </h4>
                <pre className="break-all font-mono text-xs bg-green-50 p-3 rounded border overflow-x-auto max-h-96 overflow-y-auto">
                  {JSON.stringify(apiTestResponse, null, 2)}
                </pre>
              </div>
            )}

            {apiTestError && (
              <div className="space-y-2 mt-4">
                <h4 className="font-semibold text-xs text-red-600">
                  API Error - {lastTestEndpoint}:
                </h4>
                <pre className="break-all font-mono text-xs bg-red-50 p-3 rounded border overflow-x-auto max-h-96 overflow-y-auto">
                  {String(apiTestError)}
                </pre>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default TestOAuthBack
